import { AxiosError, AxiosInstance } from 'axios'
import { DateTime } from 'luxon'
import { download } from '@invivodf/common/src/infrastructure/download.service'
import { offersSdk, Sdk } from '@b2ag/module-offers-sdk'
import type { B2bOffersSdk } from '@invivoretail/module-offers-sdk'
import { useAuthService } from '@b2ag/auth'
import { buildCallerId } from '@invivodf/front-seller-caller-id'
import { createAxiosClientWithAladinBearer } from '@/utils/axios'
import * as offerMapper from './offer.mapper'
import ImportService from './import.service'

const camelToSnake = (str) => str.replace(/[\w]([A-Z])/g, (m) => `${m[0]}_${m[1]}`).toLowerCase()

type FieldError = {
  field: string
  messages: string[]
}

const mapFieldErrorList = (fieldErrorList: FieldError[]) =>
  fieldErrorList.map((fieldError) => {
    if (fieldError.field === 'offer_reference') {
      return { ...fieldError, field: 'seller_custom_id' }
    }
    return fieldError
  })

const toAxiosLikeError = (err) =>
  err && !err.response && err.status !== undefined ? { ...err, response: { status: err.status, data: err.body } } : err

export const mapErrors = (httpError) => {
  const err = toAxiosLikeError(httpError)
  return err?.response?.data?.errors
    ? {
        ...err,
        response: {
          ...err.response,
          data: {
            ...err.response.data,
            errors: mapFieldErrorList(err.response.data.errors),
          },
        },
      }
    : err
}

class OfferService {
  private readonly productApiHost: string

  private readonly api: AxiosInstance

  private readonly sdk: Sdk

  private readonly b2bOffersSdk: B2bOffersSdk

  constructor({ productApiHost, b2bOffersSdk }) {
    this.productApiHost = productApiHost
    this.api = createAxiosClientWithAladinBearer(`${productApiHost}/v1/offers`, { timeout: 20000 })
    this.sdk = offersSdk(this.productApiHost, {
      accessTokenSource: () => useAuthService().getAccessToken(),
      callerId: buildCallerId(),
    })
    this.b2bOffersSdk = b2bOffersSdk
  }

  async listV3({
    cooperativeId,
    limit = 10 as number | undefined,
    page = 1 as number | undefined,
    filters = {},
    pagination = true,
    expiresAfter,
  }) {
    if (!cooperativeId) {
      return { totalPages: 0, offers: [] }
    }

    const selectedFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined),
    )

    const { offers, totalPages } = await this.b2bOffersSdk.getOffersForConfig({
      distributorId: cooperativeId,
      ...selectedFilters,
      ...(expiresAfter && DateTime.fromISO(expiresAfter).isValid ? { expiresAfter } : {}),
      ...(limit === Infinity ? { pagination: false } : { pagination, page, limit }),
    })

    return { totalPages, offers: offerMapper.mapListFromApi(offers) }
  }

  async upsert(offer, cooperativeId) {
    try {
      const response = await this.sdk.upsertOffer(
        encodeURIComponent(offer.id),
        cooperativeId,
        offer.variantInvivoId,
        offerMapper.mapToApiV3(offer),
      )
      if (response.is_created) {
        return { createdOffer: offerMapper.mapFromApiV3(response) }
      }
      return { updatedOffer: offerMapper.mapFromApiV3(response) }
    } catch (error) {
      const err: AxiosError = error
      return Promise.reject(mapErrors(err))
    }
  }

  async delete(offerId) {
    await this.api.delete(`${this.productApiHost}/v3/offers/${encodeURIComponent(offerId)}`)
    return true
  }

  async importFull(cooperativeId, file) {
    return ImportService.importPost(`${this.productApiHost}/backoffice/v2/offers/import`, file, {
      partner_id: cooperativeId,
      is_delta: false,
    })
  }

  async importDelta(cooperativeId, file) {
    return ImportService.importPost(`${this.productApiHost}/backoffice/v2/offers/import`, file, {
      partner_id: cooperativeId,
      is_delta: true,
    })
  }

  async importDelete(cooperativeId, file) {
    return ImportService.importDelete(`${this.productApiHost}/backoffice/v2/offers/import`, file, {
      partner_id: cooperativeId,
    })
  }

  async downloadOffers(cooperativeId, cooperativeName) {
    const response = await this.api.get(`${this.productApiHost}/backoffice/v1/cooperatives/${cooperativeId}/offers`, {
      responseType: 'blob',
      timeout: 120_000,
    })

    const extension = (await this.isPartnerWithNewOfferConfiguration(cooperativeId)) ? 'zip' : 'csv'

    download(response.data, ImportService.createFileName(cooperativeName, 'offers', extension))
  }

  async getFieldsOptions(distributorId, variantId) {
    const queryString = `partner_id=${distributorId}&variant_id=${variantId}`
    const { data } = await this.api.get(`${this.productApiHost}/v1/offers/fields-options?${queryString}`)
    return {
      prestaServicePriceTypes: data.price_type,
      salesUnitValue: data.sales_unit,
    }
  }

  async isPartnerWithNewOfferConfiguration(distributorId) {
    const { data } = await this.api.get(`${this.productApiHost}/v1/offers/${distributorId}/configuration-type`)
    return data.type !== 'legacy'
  }
}

export default OfferService
